<script lang="ts">
import { defineComponent, inject, computed, toRef } from 'vue';
import TextAreaAutoGrow from '@components/Form/TextArea/TextAreaAutoGrow.vue';
import LabeledTooltip from '@components/LabeledTooltip/LabeledTooltip.vue';
import RcButton from '@components/RcButton/RcButton.vue';
import { escapeHtml, generateRandomAlphaString } from '@shell/utils/string';
import cronstrue from 'cronstrue';
import { isValidCron } from 'cron-validator';
import { debounce } from 'lodash';
import { useLabeledFormElement, labeledFormElementProps } from '@shell/composables/useLabeledFormElement';
import { useCompactInput } from '@shell/composables/useCompactInput';
import { useVeeValidateField } from '@shell/composables/useVeeValidateField';

interface NonReactiveProps {
  onInput: (event: Event) => void | ((event: Event) => void);
}

const provideProps: NonReactiveProps = {
  onInput() {
    // noop
  },
};

export default defineComponent({
  components: {
    LabeledTooltip, RcButton, TextAreaAutoGrow
  },

  inheritAttrs: false,

  props: {
    ...labeledFormElementProps,
    /**
     * The type of the Labeled Input.
     *
     * Any native HTML input type is passed through to the underlying input
     * (e.g. text, password, number, email). A few custom values change the
     * rendering or behaviour:
     * @values cron (renders as text), multiline, multiline-password, integer (renders as text with inputmode="numeric", blocks non-integer input)
     */
    type: {
      type:    String,
      default: 'text'
    },

    /**
     * The status class of the Labeled Input and tooltip.
     * @values info, success, warning, error
     */
    status: {
      type:    String,
      default: null
    },

    /**
     * The sub-label for the Labeled Input.
     */
    subLabel: {
      type:    String,
      default: null
    },

    /**
     * Shows the human readable cron description under the input when type is
     * cron. Set to false to show it somewhere else, using the cron-hint-change
     * event to get the text.
     */
    showCronHint: {
      type:    Boolean,
      default: true
    },

    /**
     * The tooltip to display for the Labeled Input.
     */
    tooltip: {
      default: null,
      type:    [String, Object]
    },

    /**
     * Renders the tooltip when hovering the cursor over the Labeled Input.
     */
    hoverTooltip: {
      type:    Boolean,
      default: true
    },

    /**
     * Disables the password manager prompt to save the contents of the Labeled
     * Input.
     */
    ignorePasswordManagers: {
      default: false,
      type:    Boolean
    },

    /**
     * The max length of the Labeled Input.
     */
    maxlength: {
      type:    Number,
      default: null
    },

    /**
     * Hides arrows on the Labeled Input.
     * @deprecated This doesn't appear to be in use for Labeled Input.
     */
    hideArrows: {
      type:    Boolean,
      default: false
    },

    /**
     * Optionally delay on input while typing.
     */
    delay: {
      type:    Number,
      default: 0
    },

    class: {
      type:    String,
      default: ''
    },

    /**
     * Optionally use force usage of custom aria-label instead of the label for accessibility
     */
    overrideAriaLabel: {
      type:    Boolean,
      default: false
    },

    /**
     * Optionally use this to comply with a11y IF there's no label
     * associated with the input
     */
    ariaLabel: {
      type:    String,
      default: ''
    },

    /**
     * The field name used for vee-validate integration. When provided, the
     * component registers with a parent vee-validate form context
     */
    name: {
      type:    String,
      default: null
    },

    /**
     * Show a clear button when the input has a value.
     * When true, displays a keyboard-accessible clear button.
     * When false, never shows a clear button.
     * When undefined (default), shows the clear button only for type="search".
     */
    showClearButton: {
      type:    Boolean,
      default: undefined
    },

    /**
     * Accessible label for the clear button. Pass an already translated string
     * to describe what is being cleared in this specific context.
     * Falls back to the generic "Clear" translation.
     */
    clearButtonLabel: {
      type:    String,
      default: undefined
    }
  },

  emits: ['change', 'update:value', 'blur', 'update:validation', 'cron-hint-change'],

  setup(props, { emit }) {
    const {
      focused,
      onFocusLabeled,
      onBlurLabeled,
      isDisabled,
      validationMessage,
      requiredField
    } = useLabeledFormElement(props, emit);
    const { isCompact } = useCompactInput(props);

    const onInput = inject('onInput', provideProps.onInput);

    const { effectiveValidationMessage, veeHandleBlur, veeValidate } = useVeeValidateField({
      name:  toRef(props, 'name'),
      rules: toRef(props, 'rules'),
      value: toRef(props, 'value'),
      validationMessage,
    });

    const effectiveStatus = computed(() => props.status);
    const isInteger = computed(() => props.type === 'integer');
    const nativeType = computed(() => {
      if (props.type === 'cron') {
        return 'text';
      }

      if (props.type === 'integer') {
        return 'text';
      }

      return props.type;
    });

    // Hints the mobile keyboard to show a numeric layout, since the
    // integer type renders as type="text" to avoid browser number
    // formatting quirks (e.g. scientific notation for large values).
    const inputMode = computed(() => (isInteger.value ? 'numeric' : undefined));

    // Determine if clear button should be shown
    const shouldShowClearButton = computed(() => {
      // Explicit prop takes precedence
      if (props.showClearButton !== undefined) {
        return props.showClearButton && !!props.value;
      }

      // Default: show for search type when there's a value
      return props.type === 'search' && !!props.value;
    });

    return {
      focused,
      onFocusLabeled,
      onBlurLabeled,
      onInput,
      isDisabled,
      validationMessage: effectiveValidationMessage,
      requiredField,
      isCompact,
      veeHandleBlur,
      veeValidate,
      effectiveStatus,
      isInteger,
      nativeType,
      inputMode,
      shouldShowClearButton,
    };
  },

  data() {
    return {
      updated:          false,
      validationErrors: '',
      inputId:          `input-${ generateRandomAlphaString(12) }`,
      describedById:    `described-by-${ generateRandomAlphaString(12) }`
    };
  },

  computed: {
    strippedAriaLabel(): string | undefined {
      if (this.overrideAriaLabel) {
        return this.ariaLabel || undefined;
      }

      return !this.hasLabel && this.ariaLabel ? this.ariaLabel : undefined;
    },
    /**
     * Determines if the Labeled Input should display a label.
     */
    hasLabel(): boolean {
      return this.isCompact ? false : !!this.label || !!this.labelKey || !!this.$slots.label;
    },

    ariaDescribedBy(): string | undefined {
      const inheritedDescribedBy = this.$attrs['aria-describedby'];
      const internalDescribedBy = this.visibleCronHint || this.subLabel ? this.describedById : undefined;

      if (inheritedDescribedBy && internalDescribedBy) {
        return `${ inheritedDescribedBy } ${ internalDescribedBy }`;
      } else if (inheritedDescribedBy || internalDescribedBy) {
        return `${ inheritedDescribedBy || internalDescribedBy }`;
      }

      return undefined;
    },

    /**
     * Determines if the Labeled Input should display a tooltip.
     */
    hasTooltip(): boolean {
      return !!this.tooltip || !!this.tooltipKey;
    },

    tooltipValue(): string | Record<string, unknown> | undefined {
      if (this.hasTooltip) {
        return this.tooltipKey ? this.t(this.tooltipKey) : this.tooltip;
      }

      return undefined;
    },

    /**
     * Determines if the Labeled Input makes use of the suffix slot.
     */
    hasSuffix(): boolean {
      return !!this.$slots.suffix;
    },

    /**
     * Accessible label for the clear button, falling back to the generic
     * "Clear" translation when the consumer hasn't supplied a specific one.
     */
    clearButtonAriaLabel(): string {
      return this.clearButtonLabel || this.t('generic.clear');
    },

    /**
     * Determines if the Labeled Input should display a cron hint.
     */
    cronHint(): string | undefined {
      if (this.type !== 'cron' || !this.value) {
        return;
      }

      // TODO - #13202: This is required due use of 2 libraries and 3 different libraries through the code.
      const predefined = [
        '@yearly',
        '@annually',
        '@monthly',
        '@weekly',
        '@daily',
        '@midnight',
        '@hourly'
      ];
      const isPredefined = predefined.includes(this.value as string);

      // refer https://github.com/GuillaumeRochat/cron-validator#readme
      if (!isPredefined && !isValidCron(this.value as string, {
        alias:              true,
        allowBlankDay:      true,
        allowSevenAsSunday: true,
      })) {
        return this.t('generic.invalidCron');
      }

      try {
        const hint = cronstrue.toString(this.value as string || '', { verbose: true });

        return hint;
      } catch (e) {
        return this.t('generic.invalidCron');
      }
    },

    /**
     * The cron hint to display under the Labeled Input, if any.
     */
    visibleCronHint(): string | undefined {
      return this.showCronHint ? this.cronHint : undefined;
    },

    /**
     * The placeholder value for the Labeled Input.
     */
    _placeholder(): string {
      if (this.placeholder) {
        return this.placeholder.toString();
      }
      if (this.placeholderKey) {
        return this.t(this.placeholderKey);
      }

      return '';
    },

    /**
     * The max length for the Labeled Input.
     */
    _maxlength(): number | undefined {
      if (this.type === 'text' && this.maxlength) {
        return this.maxlength;
      }

      return undefined;
    },

    className() {
      return this.class;
    }
  },

  watch: {
    cronHint: {
      handler(hint: string | undefined) {
        if (this.type === 'cron') {
          this.$emit('cron-hint-change', hint);
        }
      },
      immediate: true
    }
  },

  mounted() {
    const id = this.$attrs?.id as string | undefined;

    if (id) {
      this.inputId = id;
    }
  },

  created() {
    /**
     * Determines if the Labeled Input @input event should be debounced.
    */
    this.onInput = this.delay ? debounce(this.delayInput, this.delay) : this.delayInput;
  },

  methods: {
    /**
     * Attempts to give the Labeled Input focus.
     */
    focus(): void {
      const comp = this.$refs.value as HTMLInputElement;

      if (comp) {
        comp.focus();
      }
    },

    /**
     * Attempts to select the Labeled Input.
     * @deprecated
     */
    select(): void {
      const comp = this.$refs.value as HTMLInputElement;

      if (comp) {
        comp.select();
      }
    },

    isValidIntegerInput(inputValue: string): boolean {
      const integer = /^-?[0-9]*$/;
      const nonNegativeInteger = /^[0-9]*$/;
      const pattern = Number(this.$attrs.min) >= 0 ? nonNegativeInteger : integer;

      if (!pattern.test(inputValue)) {
        return false;
      }

      const numeric = Number(inputValue);

      // Safe integer is primarily here to validate that the number fits in the datatype
      // used for numbers in the browser, if we exceed this safe number the browser automatically converts the number to scientific notation.
      if (inputValue !== '' && inputValue !== '-' && !Number.isSafeInteger(numeric)) {
        return false;
      }

      return true;
    },

    prospectiveValue(input: HTMLInputElement, inserted: string): string {
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;

      return input.value.slice(0, start) + inserted + input.value.slice(end);
    },

    onKeydown(event: KeyboardEvent): void {
      // Skip named keys (Backspace, Arrow, etc.) and modifier shortcuts (Ctrl+A, Cmd+C).
      if (!this.isInteger || event.key.length !== 1 || event.ctrlKey || event.metaKey) {
        return;
      }

      const next = this.prospectiveValue(event.target as HTMLInputElement, event.key);

      if (!this.isValidIntegerInput(next)) {
        event.preventDefault();
      }
    },

    onPaste(event: ClipboardEvent): void {
      if (!this.isInteger) {
        return;
      }

      const paste = event.clipboardData?.getData('text/plain') ?? '';
      const next = this.prospectiveValue(event.target as HTMLInputElement, paste);

      if (!this.isValidIntegerInput(next)) {
        event.preventDefault();
      }
    },

    /**
     * Emit on input change
     */
    onChange(event: Event): void {
      this.$emit('change', event);
    },

    /**
     * Emit on input with delay. Note: Arrow function is avoided due context
     * binding.
     *
     * NOTE: In multiline, TextAreaAutoGrow emits a string with the value
     * https://github.com/rancher/dashboard/issues/10249
     */
    delayInput(val: string | Event): void {
      const value = typeof val === 'string' ? val : (val?.target as HTMLInputElement)?.value;

      this.$emit('update:value', value);
    },

    /**
     * Handles the behavior of the Labeled Input when given focus.
     * @see labeled-form-element.ts mixin for onFocusLabeled()
     */
    onFocus(): void {
      this.onFocusLabeled();
    },

    /**
     * Handles the behavior of the Labeled Input when blurred and emits the blur
     * event.
     * @see labeled-form-element.ts mixin for onBlurLabeled()
     */
    onBlur(event: string | FocusEvent): void {
      this.$emit('blur', event);
      this.onBlurLabeled();
      // Mark the field as touched in vee-validate without relying on its
      // 'validated-only' guard, then run validation unconditionally so
      // errors surface on the first blur (matching useLabeledFormElement behavior).
      this.veeHandleBlur(event instanceof FocusEvent ? event : undefined, false);
      this.veeValidate();
    },

    /**
     * Clears the input value and refocuses the input field.
     */
    clearInput(): void {
      this.$emit('update:value', '');
      this.$nextTick(() => {
        this.focus();
      });
    },

    escapeHtml
  }
});
</script>

<template>
  <div
    :class="{
      'labeled-input': true,
      focused,
      [mode]: true,
      disabled: isDisabled,
      [effectiveStatus]: effectiveStatus,
      suffix: hasSuffix,
      'has-clean-tooltip': hasTooltip,
      'compact-input': isCompact,
      hideArrows,
      [className]: true
    }"
  >
    <slot name="label">
      <label
        v-if="hasLabel"
        :for="inputId"
      >
        <t
          v-if="labelKey"
          :k="labelKey"
        />
        <template v-else-if="label">{{ label }}</template>

        <span
          v-if="requiredField"
          class="required"
          :aria-hidden="true"
        >*</span>
      </label>
    </slot>

    <slot name="prefix" />

    <slot name="field">
      <TextAreaAutoGrow
        v-if="type === 'multiline' || type === 'multiline-password'"
        :id="inputId"
        ref="value"
        v-bind="$attrs"
        v-stripped-aria-label="strippedAriaLabel"
        :name="name || undefined"
        :maxlength="_maxlength"
        :disabled="isDisabled"
        :aria-disabled="isDisabled"
        :value="value || ''"
        :placeholder="_placeholder"
        autocapitalize="off"
        :class="{ 'multiline-password': type === 'multiline-password', 'auto-grow-labeled': hasLabel }"
        :aria-describedby="ariaDescribedBy"
        :aria-required="requiredField"
        @update:value="onInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <input
        v-else
        :id="inputId"
        ref="value"
        v-stripped-aria-label="strippedAriaLabel"
        :class="{ 'no-label': !hasLabel }"
        v-bind="$attrs"
        :name="name || undefined"
        :maxlength="_maxlength"
        :disabled="isDisabled"
        :aria-disabled="isDisabled"
        :type="nativeType"
        :inputmode="inputMode"
        :value="value"
        :placeholder="_placeholder"
        autocomplete="off"
        autocapitalize="off"
        :data-lpignore="ignorePasswordManagers"
        :aria-describedby="ariaDescribedBy"
        :aria-required="requiredField"
        @input="onInput"
        @keydown="onKeydown"
        @paste="onPaste"
        @focus="onFocus"
        @blur="onBlur"
        @change="onChange"
      >
    </slot>

    <slot name="suffix" />
    <!-- Clear button for search inputs -->
    <RcButton
      v-if="shouldShowClearButton"
      type="button"
      size="small"
      variant="ghost"
      left-icon="close"
      class="labeled-input-clear-button"
      :aria-label="clearButtonAriaLabel"
      :disabled="isDisabled"
      @click="clearInput"
      @keydown.enter.prevent="clearInput"
      @keydown.space.prevent="clearInput"
    />
    <!-- informational tooltip about field -->
    <LabeledTooltip
      v-if="hasTooltip"
      :hover="hoverTooltip"
      :value="tooltipValue"
      :status="status"
    />
    <!-- validation tooltip -->
    <LabeledTooltip
      v-if="!!validationMessage"
      :hover="hoverTooltip"
      :value="validationMessage"
    />
    <div
      v-if="visibleCronHint || subLabel"
      class="sub-label"
      data-testid="sub-label"
    >
      <div
        v-if="visibleCronHint"
        :id="describedById"
        role="alert"
        :aria-label="visibleCronHint"
      >
        {{ visibleCronHint }}
      </div>
      <div
        v-else-if="subLabel"
        :id="describedById"
        v-clean-html="subLabel"
      />
    </div>
  </div>
</template>
<style scoped lang="scss">
.labeled-input {
  position: relative;
}

.multiline-password:not(:focus) {
  -webkit-text-security: disc;
}

.labeled-input.view {
  input {
    text-overflow: ellipsis;
  }
}

.hideArrows {
  /* Hide arrows on number input when it overlaps with the unit */
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }
}

/* Hide the native, mouse-only search cancel button in Chrome/Safari */
input[type="search"]::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
}

// RcButton (ghost) supplies the transparent background, flex centring and focus
// outline. All that's left here is placing it in the field and sizing the target.
.labeled-input-clear-button {
  // Square 24px target (WCAG 2.5.8) - `btn-small` already gives us the height
  --rc-button-padding: 0;

  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  color: var(--input-text);

  &:hover:not(:disabled) {
    color: var(--primary);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  // RcButton renders its leftIcon with size="inherit", so pin the glyph here to
  // keep the 14px it had before, independent of the button's font-size.
  :deep(.rc-icon) {
    font-size: 14px;
  }
}

.labeled-input.suffix .labeled-input-clear-button {
  right: 40px;
}
</style>
<style>
.validation-message {
  padding: 5px;
  position: absolute;
  bottom: -35px;
}
</style>
